import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search, Users, Crown, CheckCircle2, XCircle, AlertTriangle, Calendar, Lock, Unlock, ChevronDown } from "lucide-react";
import axios from "axios";
import { base_url } from "../../utils/AxiosConfig";
import user1 from "../../images/ic_no_person_photo.svg";
import { base_url_image_view_api } from "../../utils/ImageViewingAPI";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FiCalendar } from "react-icons/fi";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import InactiveTeams from "./InactiveTeams";
dayjs.extend(relativeTime);

// ─── Custom Tooltip ────────────────────────────────────────────────────────────
const Tooltip = ({ title, children }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative inline-flex" onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
      {children}
      {visible && title && (
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap shadow-xl z-50 pointer-events-none">
          {title}
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-800 rotate-45" />
        </div>
      )}
    </div>
  );
};

// ─── Avatar ────────────────────────────────────────────────────────────────────
const Avatar = ({ src, alt, size = "md", ring = false }) => {
  const sizes = { sm: "w-7 h-7", md: "w-10 h-10", lg: "w-12 h-12", xl: "w-14 h-14" };
  return (
    <img
      src={src || user1}
      alt={alt || ""}
      onError={(e) => { e.target.src = user1; }}
      className={`${sizes[size]} rounded-full object-cover flex-shrink-0 ${ring ? "ring-2 ring-white shadow-sm" : ""}`}
    />
  );
};

// ─── Status Badge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ active }) =>
  active ? (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-500 border border-slate-200">
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" /> Inactive
    </span>
  );

// ─── Toggle Switch ─────────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={onChange}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${checked ? "bg-emerald-500" : "bg-slate-300"}`}
  >
    <span className={`inline-block h-5 w-5 rounded-full bg-white shadow-md transform transition-transform duration-200 ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
  </button>
);

// ─── Confirmation Dialog ───────────────────────────────────────────────────────
const ConfirmDialog = ({ open, member, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        <div className="bg-amber-50 px-6 py-5 border-b border-amber-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-bold text-slate-800 text-base">Member Already in Team</h3>
            <p className="text-xs text-slate-500 mt-0.5">Duplicate member warning</p>
          </div>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-slate-600 leading-relaxed">
            <span className="font-semibold text-slate-800">{member?.Name}</span>{" "}
            <span className="text-slate-400 text-xs">({member?.EmpId})</span>
            <br />
            is already a member of another team. Do you still want to add them?
          </p>
        </div>
        <div className="px-6 pb-5 flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold transition-colors"
          >
            Add Anyway
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Team Card ─────────────────────────────────────────────────────────────────
const TeamCard = ({ team, fetchProfilePic, onEdit, onToggleStatus }) => {
  const isTeamActive = team.isActive || team.ActiveStatus === "Y";
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`bg-white rounded-2xl border shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden ${isTeamActive ? "border-slate-200" : "border-slate-200 opacity-70"}`}>
      {/* Colour top strip */}
      <div className={`h-1 w-full ${isTeamActive ? "bg-gradient-to-r from-blue-500 to-blue-600" : "bg-gradient-to-r from-slate-300 to-slate-400"}`} />

      <div className="p-4">
        {/* Card Header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h3 className="font-bold text-slate-900 text-base leading-tight truncate">{team.TeamName}</h3>
            {team.TeamDescription && (
              <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{team.TeamDescription}</p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            <StatusBadge active={isTeamActive} />
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
              <Users className="w-3 h-3" /> {team.members?.length || 0}
            </span>
          </div>
        </div>

        {/* Leader Row */}
        <div className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-slate-50 border border-slate-100 mb-3">
          <Crown className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
          <Avatar src={team.imagepath ? fetchProfilePic(team.imagepath) : null} size="sm" ring />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold text-slate-800 truncate">
              {team.TeamLeaderName || team.owner?.ownerName}
            </p>
            <p className="text-xs text-slate-400">{team.TeamLeaderID || team.owner?.ownerid}</p>
          </div>
          <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-lg border border-amber-200 flex-shrink-0">
            Leader
          </span>
        </div>

        {/* Members List */}
        {team.members?.length > 0 && (
          <div className="mb-3">
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="flex items-center justify-between w-full text-xs font-semibold text-slate-500 mb-1.5 hover:text-slate-700 transition-colors"
            >
              <span>MEMBERS ({team.members.length})</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </button>
            {expanded && (
              <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1 scrollbar-none">
                {team.members.map((member) => (
                  <div
                    key={member.EmployeeID || member.employeeId || member.EmployeeName}
                    className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-xl bg-slate-50 border border-slate-100"
                  >
                    <Avatar
                      src={member.imagepath ? fetchProfilePic(member.imagepath) : null}
                      alt={member.EmployeeName || member.name}
                      size="sm"
                      ring
                    />
                    <span className="flex-1 text-xs font-medium text-slate-700 truncate">
                      {member.EmployeeName || member.name}
                    </span>
                    <span className="px-2 py-0.5 rounded-lg text-xs font-semibold bg-orange-50 text-orange-600 border border-orange-200 flex-shrink-0">
                      {member.RoleInTeam || member.role}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {!expanded && (
              <div className="flex -space-x-2">
                {team.members.slice(0, 5).map((m, i) => (
                  <Avatar
                    key={i}
                    src={m.imagepath ? fetchProfilePic(m.imagepath) : null}
                    alt={m.EmployeeName || m.name}
                    size="sm"
                    ring
                  />
                ))}
                {team.members.length > 5 && (
                  <div className="w-7 h-7 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center text-xs font-bold text-slate-500">
                    +{team.members.length - 5}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Card Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <p className="text-xs text-slate-400">
            {dayjs(team.CreatedDate || team.date).fromNow()}
            <span className="mx-1">·</span>
            {dayjs(team.CreatedDate || team.date).format("DD MMM YYYY")}
          </p>
          <div className="flex items-center gap-2">
            <Toggle
              checked={isTeamActive}
              onChange={() => onToggleStatus(team.TeamID || team.id, team.ActiveStatus || (team.isActive ? "Y" : "N"))}
            />
            <Tooltip title={isTeamActive ? "Edit team" : "Activate team to edit"}>
              <button
                disabled={!isTeamActive}
                onClick={() => onEdit(team)}
                className="w-8 h-8 flex items-center justify-center rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const TeamManagement = ({
  formData, setFormData, onSaveTeamParent,
  teams, setTeams, editingTeamBoolean, setEditingTeamBoolean, fetchActiveList,
}) => {
  const storedUserDataArray = JSON.parse(localStorage.getItem("BondTimeuserDataArray")) || [];
  const companyCode = storedUserDataArray[0]?.cmpcode || null;

  // ── State (UNCHANGED) ────────────────────────────────────────────────────────
  const [search, setSearch]                           = useState("");
  const [isOpen, setIsOpen]                           = useState(false);
  const [editingTeam, setEditingTeam]                 = useState(null);
  const [loader, setLoader]                           = useState(false);
  const [searchdata, setSearchData]                   = useState("");
  const [resetalldata, setResetallData]               = useState(false);
  const [allEmpRecords, setAllEmpRecords]             = useState([]);
  const [jobCodeList]                                 = useState([]);
  const [leaderSearch, setLeaderSearch]               = useState("");
  const [memberSearch, setMemberSearch]               = useState("");
  const [deleteModalOpen, setDeleteModalOpen]         = useState(false);
  const [teamToDelete, setTeamToDelete]               = useState(null);
  const [showConfirmDialog, setShowConfirmDialog]     = useState(false);
  const [isTeamNameEditable, setIsTeamNameEditable]   = useState(false);
  const [pendingMember, setPendingMember]             = useState(null);
  const [showMasterPopup, setShowMasterPopup]         = useState(false);
  const [masterType, setMasterType]                   = useState("");
  const [masterInputNew, setMasterInputNew]           = useState({ description: "" });
  const [masterInputEdit, setMasterInputEdit]         = useState({ description: "", transaction_id: "" });
  const [teamCodeListFromAPI, setTeamCodeListFromAPI] = useState([]);
  const [teamTypeListFromAPI, setTeamTypeListFromAPI] = useState([]);
  const [teamNameDuplicate, setTeamNameDuplicate]     = useState(false);
  const [teamNameError, setTeamNameError]             = useState("");
  const [checkingTeamName, setCheckingTeamName]       = useState(false);

  // ── Lifecycle (UNCHANGED) ────────────────────────────────────────────────────
  useEffect(() => { fetchActiveList(); getEmployeeData(); getTeamTypeList(); }, []);

  // ── Helpers (UNCHANGED) ──────────────────────────────────────────────────────
  const fetchProfilePic = (ImagePath) => {
    if (!ImagePath) return user1;
    const filePath = ImagePath.replace(/\\/g, "/");
    const fileName = filePath.split("/").pop();
    return `${base_url_image_view_api}/BONDTIME_EMP_IMG/${fileName}`;
  };

  // ── Modal actions (UNCHANGED) ────────────────────────────────────────────────
  const openAddModal = () => {
    setEditingTeamBoolean(false); setEditingTeam(null);
    setFormData((prev) => ({
      name: "", TeamName: "", Division: prev.Division, description: "", TeamDescription: "",
      teamCode: "", TeamCode: "", teamType: "", TeamType: "",
      owner: null, TeamLeaderID: "", TeamLeaderName: "", members: [],
    }));
    setIsOpen(true);
  };

  const openEditModal = (team) => {
    setEditingTeamBoolean(true); setEditingTeam(team);
    setFormData({
      ...team,
      name: team.TeamName || team.name || "", TeamName: team.TeamName || team.name || "",
      teamCode: team.TeamCode || team.teamCode || "", TeamCode: team.TeamCode || team.teamCode || "",
      teamType: team.TeamType || team.teamType || "", TeamType: team.TeamType || team.teamType || "",
      description: team.TeamDescription || team.description || "", TeamDescription: team.TeamDescription || team.description || "",
      TeamLeaderID: team.TeamLeaderID || team.owner?.ownerid || "",
      TeamLeaderName: team.TeamLeaderName || team.owner?.ownerName || "",
      owner: team.owner || { ownerid: team.TeamLeaderID, ownerName: team.TeamLeaderName },
      members: team.members?.map((m) => ({
        ...m, employeeId: m.EmployeeID || m.employeeId, EmployeeID: m.EmployeeID || m.employeeId,
        name: m.EmployeeName || m.name, EmployeeName: m.EmployeeName || m.name,
        role: m.RoleInTeam || m.role, RoleInTeam: m.RoleInTeam || m.role,
      })) || [],
    });
    setIsOpen(true);
  };

  const saveTeam = () => {
    if (!(formData.TeamName || formData.name) || !(formData?.TeamLeaderID || formData?.owner?.ownerid)) return;
    if (editingTeam) {
      setTeams((prev) => prev.map((t) => (t.TeamID || t.id) === (editingTeam.TeamID || editingTeam.id) ? { ...editingTeam, ...formData } : t));
      onSaveTeamParent(editingTeamBoolean);
    } else {
      setTeams((prev) => [...prev, { id: Date.now(), TeamID: Date.now(), ...formData }]);
      onSaveTeamParent(editingTeamBoolean);
    }
    setIsOpen(false);
  };

  const closeTeamCreationModal = () => {
    setFormData((prev) => ({
      name: "", TeamName: "", owner: null, TeamLeaderID: "", TeamLeaderName: "",
      teamCode: "", TeamCode: "", teamType: "", TeamType: "", members: [],
      date: new Date(), CreatedDate: new Date().toISOString(), isActive: true, ActiveStatus: "Y",
      Division: prev.Division,
    }));
    setTeamNameError(""); setIsOpen(false);
  };

  // ── Employee data (UNCHANGED) ────────────────────────────────────────────────
  const getEmployeeData = async () => {
    setLoader(true);
    try {
      const isSearchEmpty = !searchdata || searchdata.trim() === "";
      const searchType  = isSearchEmpty || resetalldata ? "ALL" : "FREETEXT";
      const searchParam = isSearchEmpty || resetalldata ? "ALL" : searchdata;
      const response = await axios.get(`${base_url}/api/PersonalInfoList/${companyCode}/${searchType}/YES/${searchParam}/ALL/ALL/ALL`);
      setAllEmpRecords(response.data);
    } catch (err) { console.error(err); }
    finally { setLoader(false); if (resetalldata) setResetallData(false); }
  };

  // ── Member toggle (UNCHANGED) ────────────────────────────────────────────────
  const toggleMember = async (user) => {
    const existing = formData.members?.find((m) => (m.employeeId || m.EmployeeID) === user.EmpId);
    if (existing) { setFormData({ ...formData, members: formData.members?.filter((m) => (m.employeeId || m.EmployeeID) !== user.EmpId) }); return; }
    const existsInOtherTeam = await checkMemberExistsInOtherTeam(user.EmpId);
    if (existsInOtherTeam) { setPendingMember(user); setShowConfirmDialog(true); return; }
    addMemberToTeam(user);
  };

  const checkMemberExistsInOtherTeam = async (empId) => {
    try {
      const stored = JSON.parse(localStorage.getItem("BondTimeuserDataArray")) || [];
      const cc = stored?.[0]?.cmpcode || "";
      const response = await axios.get(`${base_url}/api/DupCheck/${cc}/TEAMCOUNT/${empId}`);
      return Number(response?.data?.[0]?.COUNT || 0) > 0;
    } catch { return false; }
  };

  const addMemberToTeam = (user) => {
    setFormData({
      ...formData,
      members: [...(formData.members || []), {
        employeeId: user.EmpId, EmployeeID: user.EmpId,
        name: user.Name, EmployeeName: user.Name,
        designation: user.JobTitle || "Employee",
        role: user.Division || "Member", RoleInTeam: user.Division || "Member",
        status: "Active", employeeImage: user.imagepath || null, isActive: true,
      }],
    });
  };

  const confirmAddDuplicate = () => { if (pendingMember) { addMemberToTeam(pendingMember); setShowConfirmDialog(false); setPendingMember(null); } };

  const updateRole = (employeeId, newRole) => {
    setFormData({
      ...formData,
      members: formData?.members?.map((m) =>
        (m.employeeId || m.EmployeeID) === employeeId ? { ...m, RoleInTeam: newRole, role: newRole } : m
      ),
    });
  };

  const isEmployeeInAnyTeam = (employeeId, currentTeamId = null) =>
    teams?.some((team) => (team.TeamID || team.id) !== currentTeamId && team.members?.some((m) => (m.employeeId || m.EmployeeID) === employeeId));

  // ── Toggle team status (UNCHANGED) ──────────────────────────────────────────
  const toggleTeamStatus = async (teamId, currentStatus) => {
    try {
      const stored = JSON.parse(localStorage.getItem("BondTimeuserDataArray")) || [];
      const cc = stored?.[0]?.cmpcode || "";
      const newStatus = currentStatus === "Y" ? "N" : "Y";
      await axios.get(`${base_url}/api/CRMStatusUpdate/${cc}/TEAMACTIVE/${teamId}/${newStatus}`);
      setTeams((prev) => prev.map((t) => (t.TeamID || t.id) === teamId ? { ...t, ActiveStatus: newStatus, isActive: newStatus === "Y" } : t));
      fetchActiveList();
    } catch (err) { console.error(err); }
  };

  // ── Master APIs (UNCHANGED) ──────────────────────────────────────────────────
  const createTeamCode = () => {
    axios.post(`${base_url}/api/MasterReg/ENTRY/${companyCode}/TEAMCODE/${masterInputNew.description}/0`).then(() => { getTeamCodeList(); setMasterInputNew({ description: "" }); }).catch(console.error);
  };
  const createTeamType = () => {
    axios.post(`${base_url}/api/MasterReg/ENTRY/${companyCode}/TEAMTYPE/${masterInputNew.description}/0`).then(() => { getTeamTypeList(); setMasterInputNew({ description: "" }); }).catch(console.error);
  };
  const getTeamCodeList = async () => { const r = await axios.get(`${base_url}/api/MASTERLIST/${companyCode}/TEAMCODE/-`); if (r?.data) setTeamCodeListFromAPI(r.data); };
  const getTeamTypeList = async () => { const r = await axios.get(`${base_url}/api/MASTERLIST/${companyCode}/TEAMTYPE/-`); if (r?.data) setTeamTypeListFromAPI(r.data); };

  // ── Dup check (UNCHANGED) ────────────────────────────────────────────────────
  const checkDuplicateTeamName = async (teamName) => {
    if (!teamName?.trim()) { setTeamNameError(""); return true; }
    try {
      setCheckingTeamName(true);
      const stored = JSON.parse(localStorage.getItem("BondTimeuserDataArray")) || [];
      const cc = stored?.[0]?.cmpcode || "";
      const response = await axios.get(`${base_url}/api/DupCheck/${cc}/TEAMNAME/${formData.TeamName || formData.name}`);
      const isDuplicate = response.data[0].COUNT === 1;
      setTeamNameDuplicate(isDuplicate);
      if (isDuplicate) { setTeamNameError("Team Name Already Exists!"); return false; }
      setTeamNameError(""); return true;
    } catch { return true; }
    finally { setCheckingTeamName(false); }
  };

  // ── Derived ─────────────────────────────────────────────────────────────────
  const filteredTeams = teams?.filter((team) => {
    const s = search.toLowerCase();
    return team.TeamName?.toLowerCase().includes(s) || team.TeamLeaderName?.toLowerCase().includes(s) || team.members?.some((m) => (m.EmployeeName || m.name || "").toLowerCase().includes(s));
  });

  const filteredLeaders = [...allEmpRecords].filter((emp) => {
    const s = leaderSearch.toLowerCase();
    return emp.Name?.toLowerCase().includes(s) || emp.EmpId?.toLowerCase().includes(s) || emp.Department?.toLowerCase().includes(s) || emp.JobTitle?.toLowerCase().includes(s);
  }).sort((a, b) => {
    const lid = formData?.TeamLeaderID || formData?.owner?.ownerid;
    if (a.EmpId === lid) return -1; if (b.EmpId === lid) return 1; return 0;
  });

  const filteredMembers = allEmpRecords.filter((emp) => {
    const s = memberSearch.toLowerCase();
    const lid = formData?.TeamLeaderID || formData?.owner?.ownerid;
    return emp.EmpId !== lid && (emp.Name?.toLowerCase().includes(s) || emp.EmpId?.toLowerCase().includes(s) || emp.Department?.toLowerCase().includes(s) || emp.JobTitle?.toLowerCase().includes(s));
  }).sort((a, b) => {
    const aS = formData.members?.some((m) => (m.employeeId || m.EmployeeID) === a.EmpId);
    const bS = formData.members?.some((m) => (m.employeeId || m.EmployeeID) === b.EmpId);
    if (aS && !bS) return -1; if (!aS && bS) return 1;
    return a.Name?.localeCompare(b.Name);
  });

  const filteredTeamCodes = teamCodeListFromAPI?.filter((i) => i?.Description?.trim());
  const filteredTeamTypes = teamTypeListFromAPI?.filter((i) => i?.Description?.trim());
  const activeMasterList  = masterType === "TEAM_CODE" ? filteredTeamCodes : filteredTeamTypes;

  const disableSaveTeam  = !(formData?.TeamName || formData?.name) || !(formData?.TeamLeaderID || formData?.owner?.ownerid) || !formData?.Division || teamNameDuplicate;
  const disableEditTeam  = !(formData?.TeamName || formData?.name) || !(formData?.TeamLeaderID || formData?.owner?.ownerid);
  const isButtonDisabled = editingTeam ? disableEditTeam : disableSaveTeam;

  // ─── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 p-6 space-y-6">

      {/* ── Page Header ──────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-md">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">Team Management</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              {filteredTeams?.length ?? 0} team{filteredTeams?.length !== 1 ? "s" : ""} found
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Search */}
          {(teams?.length ?? 0) > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-300 bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all min-w-[220px]">
              <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <input
                type="text"
                placeholder="Search teams, leader, member…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 bg-transparent text-sm text-slate-700 placeholder-slate-400 outline-none"
              />
              {search && (
                <button onClick={() => setSearch("")} className="text-slate-400 hover:text-red-500 transition-colors">
                  <XCircle className="w-4 h-4" />
                </button>
              )}
            </div>
          )}

          {/* Create Team Button */}
          <Tooltip title={!formData?.Division ? "Select a Division first" : "Create a new team"}>
            <span>
              <button
                disabled={!formData?.Division}
                onClick={() => { setIsTeamNameEditable(true); openAddModal(); }}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all duration-200 shadow-sm ${
                  !formData?.Division
                    ? "bg-slate-300 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 active:bg-blue-800"
                }`}
              >
                <Plus className="w-4 h-4" />
                Create Team
              </button>
            </span>
          </Tooltip>
        </div>
      </div>

      {/* ── Empty State ───────────────────────────────────────────────────── */}
      {filteredTeams?.length === 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center justify-center py-20 px-6 text-center">
          <div className="w-20 h-20 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center mb-5">
            <Users className="w-10 h-10 text-slate-300" />
          </div>
          <h3 className="text-xl font-bold text-slate-700 mb-2">No Teams Found</h3>
          <p className="text-slate-400 text-sm max-w-sm leading-relaxed mb-6">
            {search
              ? `No teams match "${search}". Try a different search term.`
              : "There are no active teams yet. Create your first team to get started."}
          </p>
          {!search && (
            <button
              onClick={() => { setIsTeamNameEditable(true); setEditingTeam(null); setIsOpen(true); }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-all shadow-sm"
            >
              <Plus className="w-4 h-4" /> Create First Team
            </button>
          )}
        </div>
      )}

      {/* ── Team Grid ─────────────────────────────────────────────────────── */}
      {filteredTeams?.length > 0 && (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {filteredTeams.map((team) => (
            <TeamCard
              key={team.TeamID || team.id}
              team={team}
              fetchProfilePic={fetchProfilePic}
              onEdit={(t) => { setIsTeamNameEditable(false); openEditModal(t); }}
              onToggleStatus={toggleTeamStatus}
            />
          ))}
        </div>
      )}

      {/* ── Confirmation Dialog ───────────────────────────────────────────── */}
      <ConfirmDialog
        open={showConfirmDialog}
        member={pendingMember}
        onConfirm={confirmAddDuplicate}
        onCancel={() => { setShowConfirmDialog(false); setPendingMember(null); }}
      />

      {/* ── Master Popup ──────────────────────────────────────────────────── */}
      {showMasterPopup && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="font-bold text-slate-800">
                Manage {masterType === "TEAM_CODE" ? "Team Codes" : "Team Types"}
              </h3>
              <button
                onClick={() => { setShowMasterPopup(false); setMasterInputNew({ description: "" }); }}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 transition-colors"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Add new */}
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder={`New ${masterType === "TEAM_CODE" ? "team code" : "team type"}…`}
                  value={masterInputNew.description}
                  onChange={(e) => setMasterInputNew({ description: e.target.value })}
                  className="flex-1 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={masterType === "TEAM_CODE" ? createTeamCode : createTeamType}
                  disabled={!masterInputNew.description.trim()}
                  className="px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-40 transition-colors"
                >
                  Add
                </button>
              </div>

              {/* List */}
              <div className="max-h-52 overflow-y-auto space-y-1.5 pr-1 scrollbar-none">
                {activeMasterList?.length === 0 && (
                  <p className="text-center text-slate-400 text-sm py-6">No items yet</p>
                )}
                {activeMasterList?.map((item) => (
                  <div key={item.TranId || item.TransactionID} className="flex items-center justify-between px-3 py-2 rounded-xl border border-slate-100 hover:bg-slate-50 transition-colors group">
                    <span className="text-sm font-medium text-slate-700">{item.Description}</span>
                    <button
                      onClick={() => setMasterInputEdit({ description: item.Description, transaction_id: item.TranId || item.TransactionID })}
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-blue-400 hover:bg-blue-50 opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Create / Edit Modal ───────────────────────────────────────────── */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4">
          <div className="w-full max-w-4xl bg-white rounded-3xl shadow-2xl border border-slate-200 flex flex-col max-h-[90vh] overflow-hidden">

            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 flex-shrink-0">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {editingTeam ? "Edit Team" : "Create New Team"}
                </h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  {editingTeam ? "Update team details, leader, and members" : "Assign a leader and add team members"}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Date Picker */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Created On</label>
                  <div className="relative">
                    <FiCalendar className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
                    <DatePicker
                      selected={formData.CreatedDate ? new Date(formData.CreatedDate) : formData.date ? new Date(formData.date) : new Date()}
                      onChange={(date) => setFormData((prev) => ({ ...prev, CreatedDate: date?.toISOString() || "", date }))}
                      dateFormat="dd/MM/yyyy"
                      className="pr-9 pl-3 py-2 rounded-xl border border-slate-300 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 w-36"
                    />
                  </div>
                </div>
                <button
                  onClick={closeTeamCreationModal}
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body (scrollable) */}
            <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5 scrollbar-none">

              {/* Team Name */}
              <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Team Name</h3>
                  <button
                    type="button"
                    onClick={() => setIsTeamNameEditable((p) => !p)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${isTeamNameEditable ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}
                  >
                    {isTeamNameEditable ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                    {isTeamNameEditable ? "Editable" : "Locked"}
                  </button>
                </div>
                <input
                  disabled={!isTeamNameEditable}
                  placeholder="Enter team name…"
                  value={formData.TeamName || formData.name || ""}
                  onChange={(e) => { setFormData({ ...formData, TeamName: e.target.value, name: e.target.value }); setTeamNameError(""); }}
                  onBlur={() => { if (!editingTeam) checkDuplicateTeamName(formData.TeamName || formData.name); }}
                  className={`w-full px-4 py-2.5 rounded-xl border text-sm transition-all outline-none ${
                    teamNameDuplicate ? "border-red-400 bg-red-50 focus:ring-2 focus:ring-red-200"
                      : isTeamNameEditable ? "border-slate-300 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      : "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                  }`}
                />
                {checkingTeamName && (
                  <p className="mt-1.5 text-xs text-slate-400 flex items-center gap-1">
                    <span className="w-3 h-3 border border-slate-400 border-t-transparent rounded-full animate-spin" />
                    Checking availability…
                  </p>
                )}
                {teamNameError && !editingTeam && (
                  <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> {teamNameError}
                  </p>
                )}
              </div>

              {/* Job Code + Team Type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Job Code */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Job Code</label>
                  <div className="relative">
                    <select
                      value={formData.JobCode || formData.jobCode || ""}
                      onChange={(e) => setFormData({ ...formData, JobCode: e.target.value, jobCode: e.target.value })}
                      className="w-full appearance-none px-3 pr-8 py-2.5 rounded-xl border border-slate-300 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all cursor-pointer"
                    >
                      <option value="">Select Job Code</option>
                      {jobCodeList?.map((job) => (
                        <option key={job.JobCode || job.id} value={job.JobCode || job.jobCode}>
                          {(job.JobCode || job.jobCode)}{job.JobDescription ? ` - ${job.JobDescription}` : ""}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  </div>
                </div>

                {/* Team Type */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Team Type</label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <select
                        value={formData.TeamType || formData.teamType || ""}
                        onChange={(e) => setFormData({ ...formData, TeamType: e.target.value, teamType: e.target.value })}
                        className="w-full appearance-none px-3 pr-8 py-2.5 rounded-xl border border-slate-300 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all cursor-pointer"
                      >
                        <option value="">Select Type</option>
                        {filteredTeamTypes?.map((item) => (
                          <option key={item.TranId} value={item.Description}>{item.Description}</option>
                        ))}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    </div>
                    <button
                      type="button"
                      onClick={() => { setMasterType("TEAM_TYPE"); setShowMasterPopup(true); }}
                      className="px-3 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold transition-colors whitespace-nowrap"
                    >
                      + Add
                    </button>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Team Description</label>
                  <button
                    type="button"
                    onClick={() => setIsTeamNameEditable((p) => !p)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${isTeamNameEditable ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
                  >
                    {isTeamNameEditable ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                    {isTeamNameEditable ? "Editable" : "Locked"}
                  </button>
                </div>
                <textarea
                  disabled={!isTeamNameEditable}
                  rows={3}
                  placeholder="Describe this team's purpose…"
                  value={formData.TeamDescription || formData.description || ""}
                  onChange={(e) => setFormData({ ...formData, TeamDescription: e.target.value, description: e.target.value })}
                  className={`w-full px-4 py-3 rounded-xl border text-sm resize-none outline-none transition-all ${
                    isTeamNameEditable
                      ? "border-slate-300 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      : "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                  }`}
                />
              </div>

              {/* Team Leader */}
              <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                    <Crown className="w-4 h-4 text-amber-500" /> Team Leader
                  </h3>
                  {(formData?.TeamLeaderID || formData?.owner?.ownerid) && (
                    <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Selected
                    </span>
                  )}
                </div>
                {/* Leader search */}
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-300 bg-white mb-3 focus-within:ring-2 focus-within:ring-blue-500">
                  <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                  <input
                    placeholder="Search by name, ID, department…"
                    value={leaderSearch}
                    onChange={(e) => setLeaderSearch(e.target.value)}
                    className="flex-1 bg-transparent text-sm text-slate-700 placeholder-slate-400 outline-none"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1 scrollbar-none">
                  {filteredLeaders.map((emp) => {
                    const selected = formData?.TeamLeaderID === emp.EmpId || formData?.owner?.ownerid === emp.EmpId;
                    return (
                      <div
                        key={emp.EmpId}
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            TeamLeaderID: emp.EmpId, TeamLeaderName: emp.Name,
                            owner: { ownerid: emp.EmpId, ownerName: emp.Name },
                            members: prev.members?.filter((m) => (m.EmployeeID || m.employeeId || m.EmpId) !== emp.EmpId),
                          }));
                          setLeaderSearch("");
                        }}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-all ${
                          selected
                            ? "border-blue-400 bg-blue-50"
                            : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                        }`}
                      >
                        <Avatar src={emp.imagepath ? fetchProfilePic(emp.imagepath) : null} alt={emp.Name} size="md" ring />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-slate-800 truncate">
                            {emp.Name}
                            <span className="ml-2 text-xs font-normal text-slate-400">({emp.EmpId})</span>
                          </p>
                          <p className="text-xs text-slate-500 truncate">{emp.JobTitle || "Employee"} · {emp.Department || "No Department"}</p>
                        </div>
                        {selected && (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-blue-100 text-blue-700 text-xs font-bold flex-shrink-0">
                            <Crown className="w-3 h-3 text-amber-500" /> Leader
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Team Members */}
              <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-500" /> Team Members
                  </h3>
                  <span className="px-3 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold border border-blue-200">
                    {formData?.members?.length || 0} selected
                  </span>
                </div>
                {/* Member search */}
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-300 bg-white mb-3 focus-within:ring-2 focus-within:ring-blue-500">
                  <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                  <input
                    placeholder="Search members…"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    className="flex-1 bg-transparent text-sm text-slate-700 placeholder-slate-400 outline-none"
                  />
                </div>
                <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1 scrollbar-none">
                  {filteredMembers.map((user) => {
                    const selected = formData?.members?.find((m) => (m.EmployeeID || m.employeeId) === user.EmpId);
                    return (
                      <div
                        key={user.EmpId}
                        onClick={() => { setMemberSearch(""); toggleMember(user); }}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-all ${
                          selected
                            ? "border-blue-400 bg-blue-50"
                            : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                        }`}
                      >
                        {/* Checkbox */}
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${selected ? "border-blue-600 bg-blue-600" : "border-slate-300 bg-white"}`}
                        >
                          {selected && (
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <Avatar src={user.imagepath ? fetchProfilePic(user.imagepath) : null} alt={user.Name} size="md" ring />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-slate-800 truncate">
                            {user.Name}
                            <span className="ml-2 text-xs font-normal text-slate-400">({user.EmpId})</span>
                          </p>
                          <p className="text-xs text-slate-500 truncate">{user.JobTitle || "Employee"} · {user.Department || "No Department"}</p>
                        </div>
                        {/* Role selector for selected members */}
                        {selected && (
                          <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                            <select
                              value={selected.RoleInTeam || selected.role || ""}
                              onChange={(e) => updateRole(user.EmpId, e.target.value)}
                              className="appearance-none pl-2 pr-6 py-1 rounded-lg border border-blue-300 bg-blue-50 text-xs text-blue-700 font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer"
                            >
                              <option value="">Role</option>
                              {["Team Lead","Senior Employee","Employee","Intern","HR Coordinator","Supervisor"].map((r) => (
                                <option key={r} value={r}>{r}</option>
                              ))}
                            </select>
                            <ChevronDown className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 w-3 h-3 text-blue-500" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {filteredMembers.length === 0 && (
                    <div className="flex flex-col items-center py-8 text-center">
                      <Users className="w-8 h-8 text-slate-200 mb-2" />
                      <p className="text-xs text-slate-400 font-medium">No employees found</p>
                    </div>
                  )}
                </div>
              </div>

            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex-shrink-0">
              <p className="text-xs text-slate-400">
                {formData.members?.length || 0} member{formData.members?.length !== 1 ? "s" : ""} selected
                {formData?.TeamLeaderID && (
                  <span className="ml-2 text-emerald-600 font-medium">· Leader assigned ✓</span>
                )}
              </p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={closeTeamCreationModal}
                  className="px-4 py-2.5 rounded-xl border border-slate-300 text-slate-600 text-sm font-semibold hover:bg-slate-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveTeam}
                  disabled={isButtonDisabled}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all shadow-sm ${
                    isButtonDisabled
                      ? "bg-slate-300 cursor-not-allowed shadow-none"
                      : "bg-blue-600 hover:bg-blue-700 active:bg-blue-800 shadow-blue-200"
                  }`}
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {editingTeam ? "Update Team" : "Create Team"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;